import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

/**
 * Guard component to conditionally render children based on user permissions.
 * @param {string} permission - The permission key to check (e.g., 'manage_admins')
 * @param {string} action - Optional action suffix (e.g., 'edit', 'delete')
 * @param {React.ReactNode} children - Component to render if permission matches
 * @param {React.ReactNode} fallback - Optional component to render if permission fails
 */
const PermissionGuard = ({ permission, action, children, fallback = null }) => {
    const customization = useSelector((state) => state.customization);
    const userInfo = customization?.userInfo;

    if (!userInfo) return fallback;

    // Superadmin (admin role) and Vendor Owners bypass permission checks for their respective domains
    // In this platform, 'admin' role refers to the top-level platform owner/superadmin
    // 'vendor' role refers to the store owner
    if (userInfo.accountType === 'admin' || userInfo.accountType === 'vendor' || userInfo.role === 'superadmin') {
        return children;
    }

    const permissions = userInfo.permissions || [];
    const requiredPermission = action ? `${permission}:${action}` : permission;

    // Check for exact match or base permission match (e.g., 'manage_orders' allows 'manage_orders:view')
    const hasAccess = permissions.includes(requiredPermission) || 
                     (action && permissions.includes(permission)) ||
                     permissions.some(p => p.startsWith(`${requiredPermission}:`));

    return hasAccess ? children : fallback;
};

PermissionGuard.propTypes = {
    permission: PropTypes.string,
    action: PropTypes.string,
    children: PropTypes.node,
    fallback: PropTypes.node
};

export default PermissionGuard;
